<template>
  <div class="flex flex-col h-full overflow-y-auto">
    <!-- Step 0: Plan selection -->
    <div v-if="currentStep === 0" class="flex flex-col gap-3">
      <p class="text-sm bg-blue-100 p-3 rounded">
        <strong>בחירת מסלול:</strong> בחרו את מסלול הסליקה שמתאים לכם. תמיד ניתן לשנות בהמשך.
      </p>
      <div
        v-for="plan in PAYME_PLANS"
        :key="plan.id"
        @click="formData.seller_plan = plan.id"
        class="rounded-2xl border p-4 cursor-pointer transition-all"
        :class="formData.seller_plan === plan.id ? 'border-primary border-2 bg-primary-50/40' : 'border-gray-200 hover:border-gray-300'"
      >
        <div class="flex items-start justify-between gap-3">
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2">
              <span class="font-bold text-base">{{ plan.name }}</span>
              <span v-if="plan.recommended" class="text-[10px] font-semibold text-primary-700 bg-primary-100 px-2 py-0.5 rounded-full">מומלץ</span>
            </div>
            <div class="text-xs text-gray-500 mt-1 leading-snug">{{ plan.subtitle }}</div>
          </div>
          <span
            class="w-5 h-5 rounded-full flex-none box-border mt-0.5"
            :class="formData.seller_plan === plan.id ? 'border-[6px] border-primary' : 'border-2 border-gray-300'"
          />
        </div>
        <div class="font-bold mt-3">{{ plan.price }}</div>
        <ul class="mt-3 flex flex-col gap-1.5">
          <li v-for="f in plan.features" :key="f" class="flex items-center gap-2 text-[13px] text-gray-700">
            <i class="pi pi-check-circle text-green-500 !text-xs flex-none" /> {{ f }}
          </li>
        </ul>
      </div>
    </div>

    <!-- Step 1: Personal Information -->
    <div v-else-if="currentStep === 1" class="flex flex-col gap-3">
      <p class="text-sm bg-blue-100 p-3 rounded">
        <strong>שלב 1 מתוך 3:</strong> פרטים אישיים
      </p>

      <!-- First Name -->
      <div>
        <label class="font-medium mb-1 block" for="firstName">שם פרטי *</label>
        <InputText
          id="firstName"
          v-model="formData.seller_first_name"
          placeholder="שם פרטי"
          class="w-full"
        />
      </div>

      <!-- Last Name -->
      <div>
        <label class="font-medium mb-1 block" for="lastName">שם משפחה *</label>
        <InputText
          id="lastName"
          v-model="formData.seller_last_name"
          placeholder="שם משפחה"
          class="w-full"
        />
      </div>

      <!-- Social ID -->
      <div>
        <label class="font-medium mb-1 block" for="socialId">תעודת זהות *</label>
        <InputMask
          id="socialId"
          v-model="formData.seller_social_id"
          mask="999999999"
          placeholder="123456789"
          class="w-full"
        />
      </div>

      <!-- Birthdate -->
      <div>
        <label class="font-medium mb-1 block" for="birthdate">תאריך לידה *</label>
        <InputMask
          id="birthdate"
          v-model="formData.seller_birthdate"
          mask="99/99/9999"
          placeholder="dd/mm/yyyy"
          class="w-full"
        />
      </div>

      <!-- ID Issue Date -->
      <div>
        <label class="font-medium mb-1 block" for="idIssued">תאריך הנפקת תעודת זהות *</label>
        <InputMask
          id="idIssued"
          v-model="formData.seller_social_id_issued"
          mask="99/99/9999"
          placeholder="dd/mm/yyyy"
          class="w-full"
        />
      </div>

      <!-- Gender -->
      <div>
        <label class="font-medium mb-1 block">מגדר *</label>
        <div class="flex gap-4">
          <div class="flex items-center">
            <RadioButton
              v-model="formData.seller_gender"
              inputId="male"
              :value="0"
            />
            <label for="male" class="mr-2">זכר</label>
          </div>
          <div class="flex items-center">
            <RadioButton
              v-model="formData.seller_gender"
              inputId="female"
              :value="1"
            />
            <label for="female" class="mr-2">נקבה</label>
          </div>
        </div>
      </div>

      <!-- Email -->
      <div>
        <label class="font-medium mb-1 block" for="email">אימייל *</label>
        <InputText
          id="email"
          v-model="formData.seller_email"
          placeholder="email@example.com"
          type="email"
          class="w-full"
        />
      </div>

      <!-- Phone -->
      <div>
        <label class="font-medium mb-1 block" for="phone">טלפון *</label>
        <InputMask
          id="phone"
          v-model="formData.seller_phone"
          mask="999-9999999"
          placeholder="050-1234567"
          class="w-full"
        />
      </div>

    </div>

    <!-- Step 2: Business & Bank Information -->
    <div v-else-if="currentStep === 2" class="flex flex-col gap-3">
      <p class="text-sm bg-blue-100 p-3 rounded">
        <strong>שלב 2 מתוך 3:</strong> פרטי עסק ובנק
      </p>

      <!-- Business Type / Incorporation -->
      <div>
        <label class="font-medium mb-1 block">סוג עסק *</label>
        <Select
          v-model="formData.seller_inc"
          :options="incorporationTypes"
          optionLabel="label"
          optionValue="value"
          placeholder="בחר סוג עסק"
          class="w-full"
        />
      </div>

      <!-- Business ID (if not individual) -->
      <div>
        <label class="font-medium mb-1 block" for="incCode">ח.פ / ע.מ *</label>
        <InputText
          id="incCode"
          v-model="formData.seller_inc_code"
          placeholder="123456"
          class="w-full"
        />
      </div>

      <!-- Merchant Name -->
      <div>
        <label class="font-medium mb-1 block" for="merchantName">שם העסק *</label>
        <InputText
          id="merchantName"
          v-model="formData.seller_merchant_name"
          placeholder="שם העסק בעברית"
          class="w-full"
        />
      </div>

      <!-- Merchant Name English (Optional) -->
      <div>
        <label class="font-medium mb-1 block" for="merchantNameEn">שם העסק באנגלית (אופציונלי)</label>
        <InputText
          id="merchantNameEn"
          v-model="formData.seller_merchant_name_en"
          placeholder="Business Name in English"
          class="w-full"
        />
      </div>

      <!-- Business Description -->
      <div>
        <label class="font-medium mb-1 block" for="description">תיאור העסק *</label>
        <Textarea
          id="description"
          v-model="formData.seller_description"
          placeholder="תאר את השירותים שהעסק מציע"
          rows="3"
          class="w-full"
          :maxlength="255"
        />
        <small class="text-gray-500">עד 255 תווים</small>
      </div>

      <!-- Website URL -->
      <div>
        <label class="font-medium mb-1 block" for="siteUrl">כתובת אתר *</label>
        <InputText
          id="siteUrl"
          v-model="formData.seller_site_url"
          placeholder="www.example.com"
          class="w-full"
        />
      </div>

      <!-- MCC Code (auto-filled) -->
      <div>
        <label class="font-medium mb-1 block" for="mcc">קוד עסק (MCC) *</label>
        <Select
          v-model="formData.seller_person_business_type"
          :options="mccCodes"
          optionLabel="label"
          optionValue="value"
          placeholder="בחר קטגוריית עסק"
          class="w-full"
        />
      </div>

      <Divider />

      <!-- Bank Details -->
      <p class="font-semibold text-lg">פרטי בנק</p>

      <!-- Bank Code -->
      <div>
        <label class="font-medium mb-1 block" for="bankCode">קוד בנק *</label>
        <Select
          v-model="formData.seller_bank_code"
          :options="israeliBanks"
          optionLabel="label"
          optionValue="value"
          placeholder="בחר בנק"
          class="w-full"
          filter
        />
      </div>

      <!-- Bank Branch -->
      <div>
        <label class="font-medium mb-1 block" for="bankBranch">מספר סניף *</label>
        <InputNumber
          id="bankBranch"
          v-model="formData.seller_bank_branch"
          placeholder="123"
          class="w-full"
          :useGrouping="false"
        />
      </div>

      <!-- Bank Account Number -->
      <div>
        <label class="font-medium mb-1 block" for="accountNumber">מספר חשבון *</label>
        <InputText
          id="accountNumber"
          v-model="formData.seller_bank_account_number"
          placeholder="123456"
          class="w-full"
        />
      </div>
    </div>

    <!-- Step 3: Address Information -->
    <div v-else-if="currentStep === 3" class="flex flex-col gap-3">
      <p class="text-sm bg-blue-100 p-3 rounded">
        <strong>שלב 3 מתוך 3:</strong> כתובת העסק
      </p>

      <!-- City -->
      <div>
        <label class="font-medium mb-1 block" for="city">עיר *</label>
        <InputText
          id="city"
          v-model="formData.seller_address_city"
          placeholder="תל אביב"
          class="w-full"
        />
      </div>

      <!-- Street -->
      <div>
        <label class="font-medium mb-1 block" for="street">רחוב *</label>
        <InputText
          id="street"
          v-model="formData.seller_address_street"
          placeholder="רוטשילד"
          class="w-full"
        />
      </div>

      <!-- Street Number -->
      <div>
        <label class="font-medium mb-1 block" for="streetNumber">מספר *</label>
        <InputText
          id="streetNumber"
          v-model="formData.seller_address_street_number"
          placeholder="23"
          class="w-full"
        />
      </div>

<!--      &lt;!&ndash; Country (auto-filled to IL) &ndash;&gt;-->
<!--      <div>-->
<!--        <label class="font-medium mb-1 block" for="country">מדינה *</label>-->
<!--        <InputText-->
<!--          id="country"-->
<!--          v-model="formData.seller_address_country"-->
<!--          :disabled="true"-->
<!--          class="w-full"-->
<!--        />-->
<!--      </div>-->
    </div>

    <!-- Navigation Buttons -->
    <div class="w-full mt-6 flex flex-col gap-2">
      <Button
        :label="currentStep === 3 ? 'שלח בקשה' : 'הבא'"
        :disabled="!isCurrentStepValid"
        :loading="isLoading"
        @click="handleNext"
      />
      <Button
        label="הקודם"
        outlined
        class="w-full !rounded-3xl"
        @click="handlePrev"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useAdminStore } from '@app/stores';
import { useToast } from 'vue-toastification';
import { SettingsApi } from '@app/api/admin';
import InputMask from 'primevue/inputmask';
import InputNumber from 'primevue/inputnumber';
import { CreatePaymeSellerDto, PaymeSellerIncorporationType } from '@torim-app/types';
import Select from 'primevue/select';

const emit = defineEmits(['next', 'prev']);
const toast = useToast();
const adminStore = useAdminStore();

const isLoading = ref(false);
// Step 0 = plan selection; steps 1-3 = the seller-details form.
const currentStep = ref(0);

// Payme seller plans the business can pick between. The chosen `id` is sent as `seller_plan`.
const PAYME_PLANS = [
  {
    id: 'VPLN1765-380141JS-G1P3D4UY-45DRVGKC',
    name: 'לא סלקת, לא שילמת',
    subtitle: 'למי שרוצים את האופציה לסלוק מדי פעם בראש שקט.',
    price: 'ללא דמי מנוי',
    features: ['עמלת אשראי סטנדרטית והוגנת', '‎+ 1 ש"ח דמי סליקה לעסקה בלבד'],
    recommended: false,
  },
  {
    id: 'VPLN1765-788645KX-XPCOXFEM-1RM77JNP',
    name: 'המסלול המושלם לעסקים',
    subtitle: 'הבחירה של עסקים שסולקים ביום-יום ולא רוצים התעסקות נוספת.',
    price: '‎89 ש"ח / לחודש',
    features: ['עמלת אשראי בלבד (ללא מינימום)', 'חיבור מלא למערכת חשבוניות', '200 חשבוניות בחודש כלולות'],
    recommended: true,
  },
];

// Auto-scroll to top when step changes
watch(currentStep, () => {
  // Wait for next tick to ensure DOM is updated
  setTimeout(() => {
    // PrimeVue Drawer uses .p-drawer-content class for its scrollable content
    const drawerContent = document.querySelector('.p-drawer-content');
    if (drawerContent) {
      drawerContent.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, 0);
});

// Seller Incorporation Types enum
const incorporationTypes = [
  { value: PaymeSellerIncorporationType.EXEMPT_COMPANY, label: 'עוסק פטור' },
  { value: PaymeSellerIncorporationType.SOLE_PROPRIETORSHIP, label: 'עוסק מורשה' },
  { value: PaymeSellerIncorporationType.INCORPORATED_COMPANY, label: 'חברה בע"מ' },
];

// Israeli Banks
const israeliBanks = computed(() => {
  return import.meta.env.VITE_APPLE_SIGN_IN_CLIENT_ID?.includes('stg') ? [
    { value: 54, label: 'בנק טסט' }
  ] : [
    { value: 10, label: 'לאומי' },
    { value: 12, label: 'הפועלים' },
    { value: 4, label: 'יהב' },
    { value: 11, label: 'דיסקונט' },
    { value: 20, label: 'מזרחי טפחות' },
    { value: 9, label: 'הדואר' },
    { value: 31, label: 'בינלאומי' },
    { value: 54, label: 'מרכנתיל דיסקונט' },
    { value: 46, label: 'מסד' },
    { value: 17, label: 'מרכנתיל' }
  ];
});

// Common MCC codes for beauty/salon businesses
const mccCodes = [
  { value: 7230, label: 'מספרות וטיפוח' },
  { value: 7298, label: 'ספא ובריאות' },
  { value: 7297, label: 'שירותי עיסוי' },
  { value: 5977, label: 'קוסמטיקה וטיפוח' },
  { value: 8099, label: 'שירותי בריאות אחרים' }
];

// Form data
const formData = ref<CreatePaymeSellerDto>({
  // Personal Info
  seller_first_name: '',
  seller_last_name: '',
  seller_social_id: '',
  seller_birthdate: '',
  seller_social_id_issued: '',
  seller_gender: 1,
  seller_email: '',
  seller_phone: '',

  // Business & Bank Info
  seller_inc: PaymeSellerIncorporationType.EXEMPT_COMPANY,
  seller_inc_code: '',
  seller_merchant_name: '',
  seller_merchant_name_en: '',
  seller_description: '',
  seller_site_url: '',
  seller_person_business_type: 7230, // Default: Beauty/Salon
  seller_bank_code: undefined,
  seller_bank_branch: undefined,
  seller_bank_account_number: '',

  // Address Info
  seller_address_city: '',
  seller_address_street: '',
  seller_address_street_number: '',
  seller_address_country: 'IL',

  // Seller plan — defaults to the pay-as-you-go plan.
  seller_plan: PAYME_PLANS[0].id
});

// Pre-fill with business data
onMounted(() => {
  const business = adminStore.getBusinessSiteData;
  if (business) {
    formData.value.seller_merchant_name = business.title || '';
    formData.value.seller_site_url = `https://time-it.co.il/business/${business.bid}`;
    SettingsApi.getPrefilledInfo().then(res => {
      formData.value.seller_email = res.email;
      formData.value.seller_phone = res.phoneNumber.split('-').join('');
    });
  }
});

// Helper function to format phone to PayMe format
function formatPhoneToPayMe(phone: string): string {
  // Remove all non-digits
  const digits = phone.replace(/\D/g, '');

  // If starts with 0, replace with +972
  if (digits.startsWith('0')) {
    const rest = digits.substring(1);
    return `+972-${rest.substring(0, 2)}-${rest.substring(2)}`;
  }

  // If already has country code
  if (digits.startsWith('972')) {
    const rest = digits.substring(3);
    return `+972-${rest.substring(0, 2)}-${rest.substring(2)}`;
  }

  // Default: add +972 prefix
  return `+972-${digits.substring(0, 2)}-${digits.substring(2)}`;
}

// Helper function to clean phone for API (remove dashes)
function cleanPhoneForApi(phone: string): string {
  return formatPhoneToPayMe(phone).replace(/-/g, '');
}

// Validation for each step
const isStep1Valid = computed(() => {
  return !!(
    formData.value.seller_first_name &&
    formData.value.seller_last_name &&
    formData.value.seller_social_id &&
    formData.value.seller_social_id.replace(/_/g, '').length === 9 &&
    formData.value.seller_birthdate &&
    formData.value.seller_birthdate.includes('/') &&
    formData.value.seller_social_id_issued &&
    formData.value.seller_social_id_issued.includes('/') &&
    formData.value.seller_gender !== null &&
    formData.value.seller_email &&
    formData.value.seller_phone
  );
});

const isStep2Valid = computed(() => {
  const baseValid = !!(
    formData.value.seller_inc !== null &&
    formData.value.seller_merchant_name &&
    formData.value.seller_description &&
    formData.value.seller_site_url &&
    formData.value.seller_person_business_type &&
    formData.value.seller_bank_code !== null &&
    formData.value.seller_bank_branch !== null &&
    formData.value.seller_bank_account_number &&
    !!formData.value.seller_inc_code
  );

  return baseValid;
});

const isStep3Valid = computed(() => {
  return !!(
    formData.value.seller_address_city &&
    formData.value.seller_address_street &&
    formData.value.seller_address_street_number &&
    formData.value.seller_address_country
  );
});

const isCurrentStepValid = computed(() => {
  switch (currentStep.value) {
    case 0:
      return !!formData.value.seller_plan;
    case 1:
      return isStep1Valid.value;
    case 2:
      return isStep2Valid.value;
    case 3:
      return isStep3Valid.value;
    default:
      return false;
  }
});

// Navigation handlers
const handleNext = async () => {
  if (currentStep.value < 3) {
    currentStep.value++;
  } else {
    await submitForm();
  }
};

const handlePrev = () => {
  if (currentStep.value > 0) {
    currentStep.value--;
  } else {
    emit('prev');
  }
};

// Submit form
const submitForm = async () => {
  if (!isCurrentStepValid.value) {
    toast.error('אנא מלא את כל השדות הנדרשים');
    return;
  }

  isLoading.value = true;

  try {
    // Clean phone numbers before sending
    const payload = {
      ...formData.value,
      seller_phone: cleanPhoneForApi(formData.value.seller_phone)
    };

    await SettingsApi.createPaymeSeller(payload);
    emit('next');
  } catch (error: any) {
    console.error('PayMe seller creation error:', error);
    toast.error(error?.response?.data?.message || 'שגיאה ביצירת חשבון PayMe');
  } finally {
    isLoading.value = false;
  }
};
</script>

<style scoped lang="scss">
</style>
